/**
 * Campaign attribution for a registration link, emitted as the standard
 * `utm_*` query params. The register screen already captures these off the
 * query string (`getUtmAttribution`) and signup persists them to
 * `users/{uid}.marketing.utm`, so a link tagged here is attributable end to
 * end without any further wiring.
 *
 * Tagging lives in this builder rather than at each call site so every surface
 * that hands out a link spells the params the same way.
 */
export type RegistrationUtmParams = {
  /** Where the link lives, e.g. `impact-report`, `flyer`. */
  source?: string;
  /** How it was delivered, e.g. `qr`, `social`, `text`. */
  medium?: string;
  /** The campaign the link belongs to, e.g. `organization-flyer`. */
  campaign?: string;
  /** Which specific link or creative within that campaign. */
  content?: string;
};

export type RegistrationUrlParams = {
  cohortId?: string;
  organizationId?: string;
  invitationCode?: string;
  /**
   * Vocation slug (e.g. `diesel_tech`, `hvac`) that pre-selects the funnel on
   * the register screen. Omit for the default CDL journey. The register screen
   * treats it as advisory — an invalid or inactive vocation falls back to CDL.
   */
  vocation?: string;
  /**
   * Campaign attribution. Omit for an untagged link — callers that predate
   * tagging keep the exact URL they emitted before.
   */
  utm?: RegistrationUtmParams;
};

const UTM_FIELDS = [
  ["source", "utm_source"],
  ["medium", "utm_medium"],
  ["campaign", "utm_campaign"],
  ["content", "utm_content"],
] as const;

/**
 * Build the relative registration URL used by browser and server-side referral
 * surfaces. Keeping the path relative lets each caller choose the correct
 * origin for its environment.
 */
export default function buildRegistrationUrl(
  params: RegistrationUrlParams
): string {
  const searchParams = new URLSearchParams();

  if (params.cohortId) {
    searchParams.set("cohortId", params.cohortId);
  }

  if (params.organizationId) {
    searchParams.set("organizationId", params.organizationId);
  }

  if (params.invitationCode) {
    searchParams.set("invitationCode", params.invitationCode);
  }

  if (params.vocation) {
    searchParams.set("vocation", params.vocation);
  }

  // Attribution trails the functional params so the recognisable part of the
  // link (the code, the org) stays readable at the front of a printed URL.
  UTM_FIELDS.forEach(([field, queryParam]) => {
    const value = params.utm?.[field];
    if (value) {
      searchParams.set(queryParam, value);
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `/register?${queryString}` : "/register";
}
